import { useCallback, useEffect, useState } from "react";

import { BillingModel } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { DateTime } from "luxon";

import { useErrorSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { consoleErrorWithSentry } from "../../../utils";
import { type GCPTableUpdate } from "./types";

const startOfLastMonth = DateTime.utc().startOf("month").minus({ months: 1 }).toJSDate();

export const useGCPTableUpdates = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<GCPTableUpdate[]>([]);
  const showError = useErrorSnackbar(7);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const rowsSnaps = await getCollection(BillingModel)
        .doc("manualOperations")
        .collection("gcpTableUpdates")
        .where("createdAt", ">=", startOfLastMonth)
        .orderBy("createdAt", "desc")
        .get();

      const rowsPromises = rowsSnaps.docs.map(async (rowDocSnap) => {
        const row = rowDocSnap.asModelData();
        const customerModel = row.customer && (await row.customer.get()).asModelData();

        return {
          ...row,
          id: rowDocSnap.id,
          customerName: customerModel?.primaryDomain as string,
        };
      });

      const rows = await Promise.all(rowsPromises);

      setRows(rows);
      setLoading(false);
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : "error");
      consoleErrorWithSentry(err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadRows();
  }, [loadRows, showError]);

  return { rows, loadRows, loading };
};
